import { useCallback, useMemo, useState } from 'react';

import { useOfficeEditingCtx } from '@/app/(app)/settings/office/provider';
import { DayScheduleEditor, DaySlot } from '@/components/molecules/day-schedule-editor';
import { ApiSchemas } from '@/components/providers/v1-api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DayOfWeek, DAYS_OF_WEEK } from '@/consts/date-time';
import { groupBy } from '@/lib/group';
import { createUUID } from '@/lib/uuid';

type OfficeScheduling = ApiSchemas['OfficeSettingsSchedulingUpsert'];

export default function OfficeScheduling() {
  const { office, update } = useOfficeEditingCtx();

  const onChangeScheduling = useCallback(
    (v: Partial<OfficeScheduling>) => {
      if (office.settings_scheduling) {
        update({ settings_scheduling: { ...office.settings_scheduling, ...v } });
      } else {
        update({ settings_scheduling: { ...v } });
      }
    },
    [office, update]
  );

  return (
    <div className='flex w-full flex-col gap-14'>
      <div className='flex flex-col gap-4'>
        <h2 className='text-heading-md'>Scheduling Settings</h2>
        <div className='grid gap-2 md:grid-cols-2'>
          <div className='space-y-2'>
            <Label>Max Appointments Per Day</Label>
            <InputNumber
              placeholder='0'
              value={office.settings_scheduling?.max_service_events_per_day ?? null}
              setValue={(value) => onChangeScheduling({ max_service_events_per_day: value })}
            />
          </div>
        </div>
      </div>
      <ArrivalWindows
        value={office.settings_scheduling?.arrival_windows ?? []}
        setValue={(value) => onChangeScheduling({ arrival_windows: value })}
      />
    </div>
  );
}

interface InputNumberProps {
  placeholder?: string;
  value: number | null;
  setValue: (value: number | null) => void;
}

function InputNumber({ value, setValue, ...props }: InputNumberProps) {
  const [text, setText] = useState<string>(value?.toString() || '');

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
    setValue(e.target.value ? Number(e.target.value) : null);
  };

  return (
    <Input type='number' value={text} onChange={onChange} pattern='[0-9]*' placeholder={props.placeholder} {...props} />
  );
}

interface ArrivalWindowsProps {
  value: ApiSchemas['ArrivalWindow'][];
  setValue: (value: ApiSchemas['ArrivalWindow'][]) => void;
}

function ArrivalWindows({ value, setValue }: ArrivalWindowsProps) {
  const grouped = useMemo(() => {
    const groupedByDay = groupBy(value, 'day_of_week');
    return DAYS_OF_WEEK.map((day) => ({
      day_of_week: day,
      slots: (groupedByDay.get(day) || [])
        .map((slot) => ({ ...slot, id: createUUID() }))
        .sort((a, b) => a.start_time.localeCompare(b.start_time)),
    }));
  }, [value]);
  const updateDaySlots = useCallback(
    (day: DayOfWeek, slots: DaySlot[]) => {
      const newDays = value.filter((d) => d.day_of_week !== day);
      const newSlots = slots.map((slot) => ({
        ...slot,
        day_of_week: day,
      }));
      setValue([...newDays, ...newSlots]);
    },
    [value, setValue]
  );

  return (
    <div className='flex flex-col gap-4'>
      <h2 className='text-heading-md'>Default Arrival Windows</h2>
      <div className='flex flex-col'>
        {grouped.map((day) => (
          <DayScheduleEditor
            key={day.day_of_week}
            schedule={day}
            update={(slots) => updateDaySlots(day.day_of_week, slots)}
          />
        ))}
      </div>
    </div>
  );
}
